import React, { useCallback, useEffect, useState } from 'react';
import './ProjectManage.css';
import { Button, Dropdown, Form, Modal, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { FaCheck, FaFileDownload } from "react-icons/fa";
import { RiFileEditFill } from 'react-icons/ri';


const ProjectManagement = () => {
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentProject, setCurrentProject] = useState(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonContent, setJsonContent] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const fetchProjects =useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.projects.getAll();
      
      if (response.data.status === 'success' && Array.isArray(response.data.projects)) {
        return response.data.projects;
      } else {
        toast.info(response.data.message || 'No projects available');
        return [];
      }
    } catch (error) {
      toast.error(error.message || 'Failed to fetch projects');
      return [];
    } finally {
      setLoading(false);
    }
  },[]);

  const updateProjectStatus = async (projectId, newStatus) => {
    try {
      const response = await api.projects.updateStatus({
        id: projectId,
        status: newStatus
      });
      
      if (response.data.status === 'success') {
        toast.success('Project status updated successfully');

        setProjects(prevProjects => 
          prevProjects.map(project => 
            project.project_id === projectId 
              ? { ...project, status: newStatus } 
              : project
          )
        );
      } else {
        throw new Error(response.data.message || 'Failed to update project status');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update project status');
    }
  };

  const checkProcessingProjects = useCallback(async () => {
    try {
      const response = await api.projects.getAll();
      
      if (response.data.status === 'success' && Array.isArray(response.data.projects)) {
        setProjects(prevProjects => {
          let hasChanges = false;
          
          const updatedProjects = prevProjects.map(project => {
            const updatedProject = response.data.projects.find(
              p => p.project_id === project.project_id
            );
            
            if (updatedProject && 
                project.status === 'Processing' && 
                updatedProject.status !== 'Processing') {
              hasChanges = true;
              return updatedProject;
            }
            
            return project;
          });
          
          return hasChanges ? updatedProjects : prevProjects;
        });
      }
    } catch (error) {
      console.error('Failed to check processing projects:', error);
    }
  }, []);
  
  useEffect(() => {
    const hasProcessingProjects = projects.some(project => project.status === 'Processing');
    
    if (hasProcessingProjects) {
      const interval = setInterval(checkProcessingProjects, 10000); // Check every 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [projects, checkProcessingProjects]);

  const deleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }
    
    try {
      const response = await api.projects.deleteProject({id});
      
      if (response.data.status === 'success') {
        toast.success('Project deleted successfully');
        setProjects(prevProjects => 
          prevProjects.filter(project => project.project_id !== id)
        );
      } else {
        throw new Error(response.data.message || 'Failed to delete project');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete project');
    }
  };

  const downloadXml = async (id) => { 
    try {
      const response = await api.projects.downloadXml({id});
      
      if (response.data.status === 'success') {
        if (response.data.download_url) {
          
          const link = document.createElement('a');
          link.href = response.data.download_url;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('XML file downloaded successfully');
        }else{
          throw new Error(response.data.message || 'Failed to download project XML');
        }
      } else {
        throw new Error(response.data.message || 'Failed to download project xml');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to download project');
    }
  };

  const handleFileClick = async (id, fileName) => {
    try {
      toast.info('Opening file...');
      const response = await api.projects.viewProjectpdf({id});
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error(response.data.message || 'File URL is not available');
      }
    } catch (error) {
      console.error('Error accessing file:', error);
      toast.error('Failed to access project file');
    }
  };

  const downloadJson = async (id) => { 
    try {
      const response = await api.projects.downloadJson({id});
      
      if (response.data.status === 'success') {
        if (response.data.download_url) {
          
          const link = document.createElement('a');
          link.href = response.data.download_url;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('JSON file downloaded successfully');
        }else{
          throw new Error(response.data.message || 'Failed to download project JSON file');
        }
      } else {
        throw new Error(response.data.message || 'Failed to download project JSON file');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to download project');
    }
  };

  const handleEditJsonWithNotification = async (projectId) => {
    await handleEditJson(projectId);
  };

  const handleEditJson = async(id) =>{
    try {
      setJsonError('');
      const project = projects.find(p => p.project_id === id);
      setCurrentProject(project);
      setShowJsonModal(true);
      setIsModalLoading(true);
      const response = await api.projects.getJsonContent({id});
      if (response.data.status === 'success') {
          setTimeout(() => {
            setJsonContent(JSON.stringify(response.data.intermediate_json, null, 2));
            setIsModalLoading(false);
          }, 100); 

      }else {
        setJsonError('Failed to load JSON content. Please try again.');
        throw new Error(response.data.message || 'Failed to download project JSON file');
      }
    } catch (error) {
      console.error('Error accessing file:', error);
      toast.error('Failed to access project file');
    }finally {
      setIsModalLoading(false);
    }
  }

  // Handle modal close
  const handleCloseModal = () => {
    setShowJsonModal(false);
    setCurrentProject(null);
    setJsonContent('');
    setJsonError('');
  };

  // Handle JSON content change
  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonContent(value);
    try {
      JSON.parse(value); // Validate JSON
      setJsonError(''); // Clear error if valid
    } catch (error) {
      setJsonError('Invalid JSON format. Please correct it.');
    }
  };

  // Handle save JSON
  const handleSaveJson = async () => {
      if (jsonError || !currentProject) return;
      
      setIsSaving(true);
      
      try {
        const parsedJson = JSON.parse(jsonContent);
        const response = await api.projects.updateJsonContent({
          id: currentProject.project_id,
          intermediate_json: parsedJson
        });
        if(response?.data?.status ==='success'){
          setProjects(prevProjects =>
            prevProjects.map(project =>
              project.project_id === currentProject.project_id
                ? { ...project, 
                    is_json_edited: 1,
                    status: 'Processing'
                  } 
                : project
            )
          );
          setShowJsonModal(false);
          toast.success('JSON saved successfully');
        }
      } catch (error) {
        console.error('Error saving JSON:', error);
        toast.error('Failed to save JSON. Please try again.');
      } finally {
        setIsSaving(false);
      }
  };

  const handleStatusChange = (project_id, status) => {
    updateProjectStatus(project_id, status);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Processing':
        return 'info';
      case 'Completed':
        return 'warning';
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Failed':
        return 'fail';
      default:
        return '';
    }
  };

  const isDownloadDisabled = (status) => {
    return status === 'Processing'|| status === 'Failed';
  };

  const isRemoveDisabled = (status) => {
    return status === 'Processing';
  };

  useEffect(() => {
    const loadProjects = async () => {
      const data = await fetchProjects();
      setProjects(data);
      setLoading(false);
    };
    loadProjects();
  }, [fetchProjects]);


  if (loading) {
    return <div className='loading'>Loading projects...</div>;
  }

  return (
    <>
    <div className="d-flex flex-column manage-container">
      <div className="d-flex justify-content-between mb-3 ">
        <div className='manage-project'>Manage Project</div>
        <Button className='create-project'
        onClick={() => navigate('/home/dashboard')} >Create Project</Button>
      </div>
      <Table responsive className='manage-project-table'>
        <thead >
          <tr >
            <th style={{ minWidth: '200px' }}>Project Name</th>
            <th style={{ minWidth: '250px' }}>File Name</th>
            <th style={{ minWidth: '130px' }}>Date</th>
            <th style={{ minWidth: '100px' }}>Mode</th>
            <th style={{ minWidth: '100px' }}>Page Numbers</th>
            <th style={{ minWidth: '100px' }}>Status</th>
            <th style={{ minWidth: '70px' }}>Valid</th>
            <th style={{ minWidth: '150px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project, index) => (
            <tr key={index}>
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{project?.project_name}</td>
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <span 
                  onClick={() => handleFileClick(project.project_id, project.file_name)}
                  style={{ 
                    color: '#007bff', 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    display: 'inline-block'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#0056b3'}
                  onMouseLeave={(e) => e.target.style.color = '#007bff'}
                  title="Click to open file"
                >
                  {project?.file_name}
                </span>
              </td>
              <td>{project?.created_date}</td>
              <td>{project?.dmx_mode ? project.dmx_mode.charAt(0).toUpperCase() + project.dmx_mode.slice(1) : ''}</td>
              <td>{project?.page_numbers}</td>
              <td>
                <div className={
                  getStatusClass(project.status) +' status-badge' 
                }>
                  {project.status ==='Processing' || project.status ==='Failed'?(<span>{project.status}</span>) :
                  (
                  <Dropdown>
                  <Dropdown.Toggle>
                    <span >
                    {project.status}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {[ 'Approved', 'Rejected'].map((status) => (
                      <Dropdown.Item
                        key={status}
                        // onClick={() => handleStatusChange(index, status)}
                        onClick={() => handleStatusChange(project.project_id, status)}
                      >
                        {status}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                  </Dropdown>
                  )}
                </div>
              </td>
              <td className="center-icon">
                {project.valid ? (
                  <>
                    <span data-tooltip-id="tooltip-valid" data-tooltip-content="Validation successful">
                      <img src="/assets/icons/valid.svg" alt="valid" />
                    </span>
                    <Tooltip id="tooltip-valid" />
                  </>
                ) : (
                  <>
                    <span data-tooltip-id="tooltip-invalid" data-tooltip-content="Validation failed">
                      <img src="/assets/icons/invalid.svg" alt="invalid" />
                    </span>
                    <Tooltip id="tooltip-invalid" />
                  </>
                )}
              </td>
              <td>
                <div className='d-flex'>
                <div className="position-relative me-2">
                  <Button size="sm" className={`btn-download me-0`}
                    disabled={isDownloadDisabled(project.status)}
                    onClick={() => handleEditJsonWithNotification(project.project_id)}
                    title="Edit JSON file"
                    style={{
                      backgroundColor: '#28a745', 
                    }}
                    >
                      <RiFileEditFill />  EDIT
                  </Button>
                  {project.is_json_edited === 1 && (
                    <div 
                      className="position-absolute"
                      style={{
                        top: '-4px',
                        right: '-1px',
                        backgroundColor: '#28a745',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        opacity: isDownloadDisabled(project.status) ? 0.6 : 1 
                      }}
                    >
                    <FaCheck size={10} color="white" />
                  </div>
                  )}
                  </div>
                  <Button size="sm" className="btn-download me-2"
                  disabled={isDownloadDisabled(project.status)}
                  onClick={() => downloadJson(project.project_id)}
                  title="Download JSON file"
                  >
                    <FaFileDownload /> JSON
                  </Button>
                  <Button size="sm" className="btn-download me-2"
                  disabled={isDownloadDisabled(project.status)}
                  onClick={() => downloadXml(project.project_id)}
                  title="Download XML file"
                  >
                    <FaFileDownload /> XML
                  </Button>
                  <Button 
                    variant="danger"
                    className='btn-remove'
                    disabled={isRemoveDisabled(project.status)}
                    onClick={() => deleteProject(project.project_id)}
                  >
                    <img src='/assets/icons/remove.svg' alt='Remove'/>
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>

    <Modal show={showJsonModal} onHide={handleCloseModal}  dialogClassName="custom-modal-size" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Edit JSON - {currentProject?.project_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        { isModalLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading JSON content...</p>
            </div>
          ) : (
            <>
              <Form.Group>
                <Form.Label>JSON Content</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={20}
                  value={jsonContent}
                  onChange={handleJsonChange}
                  isInvalid={!!jsonError}
                  style={{ 
                    fontFamily: 'monospace',
                    fontSize: '14px'
                  }}
                  placeholder="Enter valid JSON content..."
                />
                {jsonError && (
                  <Form.Control.Feedback type="invalid">
                    {jsonError}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <div className="mt-2">
                <small className="text-muted">
                  Make sure your JSON is properly formatted before saving.
                </small>
              </div>
            </>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
            variant="secondary" 
            onClick={handleCloseModal}
            disabled={isSaving}
        >
            Cancel
        </Button>
        <Button 
            variant="primary" 
            onClick={handleSaveJson}
            disabled={!!jsonError || isModalLoading || isSaving || !jsonContent.trim()}
        >
        {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default ProjectManagement